import React, { useState, useEffect, useRef } from "react";
import Skeleton from "../Skeleton";

/**
 * A wrapper for Elfsight widgets that displays a skeleton loading state
 * until the widget content is actually injected by the third-party script.
 */
const ElfsightWidget = ({ children, columns = 3, rows = 1 }) => {
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // MutationObserver to detect when Elfsight injects its content
    const observer = new MutationObserver(() => {
      // 1. Recursive function to find and hide the branding (even in Shadow DOM)
      const hideBranding = (root) => {
        const branding = root.querySelector('a[href*="elfsight.com"]');
        if (branding) {
          branding.style.setProperty("display", "none", "important");
          branding.style.setProperty("opacity", "0", "important");
          branding.style.setProperty("visibility", "hidden", "important");
        }

        // Check if any children are shadow hosts
        const allElements = root.querySelectorAll("*");
        allElements.forEach((el) => {
          if (el.shadowRoot) hideBranding(el.shadowRoot);
        });
      };

      hideBranding(container);

      // 2. Check if any children were added (Elfsight components)
      if (container.children.length > 1) { 
        setLoading(false);
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    // Disconnect after 10 seconds to save performance
    const cleanupTimeout = setTimeout(() => {
      observer.disconnect();
    }, 10000);

    // Backup timeout for loading state
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
    }, 5000);

    return () => {
      observer.disconnect();
      clearTimeout(cleanupTimeout);
      clearTimeout(loadingTimeout);
    };
  }, []);

  return (
    <div className="elfsight-widget-wrapper" ref={containerRef} style={{ position: "relative" }}>
      {/* Skeleton Layer */}
      {loading && (
        <div className="elfsight-skeleton-container" style={{marginTop:"1rem"}}>
          {Array.from({ length: columns * rows }).map((_, i) => (
            <div key={i} className="skeleton-card-group">
              <Skeleton type="card" className="skeleton--rect" style={{ height: "280px", marginBottom: "1rem", borderRadius: "12px" }} />
              <Skeleton type="text" style={{ width: "80%", height: "24px" }} />
              <Skeleton type="text" style={{ width: "50%", height: "16px" }} />
            </div>
          ))}
        </div>
      )}

      {/* Actual Widget Content */}
      <div className={loading ? "fadeOut" : "fadeIn"} style={{ transition: "opacity 0.4s ease" }}>
        {children}
      </div>
    </div>
  );
};

export default ElfsightWidget;
